import React, { useState } from "react";
import { JSONTree } from "react-json-tree";
import styled from "styled-components";
import { ReactComponent as Add } from "../../../assets/images/icons/tree-add.svg";
import { ReactComponent as Remove } from "../../../assets/images/icons/tree-remove.svg";
import { IconButton } from "@mui/material";
import { StyledMenu, StyledMenuItem } from "../style";
import { JsonViewTheme } from "./utils";

// Define a styled container
const TreeContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;
  padding: 10px;
  background-color: transparent; // Make background transparent
`;

// Props interface
interface JsonViewerProps {
  jsonData: any; // Accept any dynamic JSON data
  theme?: any; // Optional theme prop for customization
  shouldExpandNode?: (keyName: any, data: any, level: number) => boolean; // Function for expanding nodes
  handleSelectionChange?: any;
  contactProperties?: any[]; // Only keep the contactProperties
}

// Main component
const JsonViewer: React.FC<JsonViewerProps> = ({
  contactProperties = [],
  jsonData,
  theme,
  shouldExpandNode,
  handleSelectionChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null); // State for menu anchor
  const [currentPath, setCurrentPath] = useState<string>(""); // State to hold the current path
  const [nestedMenuOpen, setNestedMenuOpen] = useState<boolean>(false); // State for nested menu visibility
  const [parentOption, setParentOption] = useState<string>(""); // State for the currently selected parent option

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    path: string
  ) => {
    setCurrentPath(path); // Set the current path
    setAnchorEl(event.currentTarget); // Set the anchor element for the menu
  };

  const handleMenuClose = () => {
    setAnchorEl(null); // Close the menu
    setNestedMenuOpen(false); // Reset nested menu state
    setParentOption(""); // Reset selected parent option
  };

  // Simplified handleMenuItemClick
  const handleMenuItemClick = (action: string) => {
    setParentOption(action); // Set the selected parent option
    setNestedMenuOpen(true); // Open the nested menu
  };

  // Handle nested menu item click
  const handleNestedMenuItemClick = (
    nestedAction: string,
    isRemove: boolean
  ) => {
    handleSelectionChange(nestedAction, currentPath, isRemove, parentOption); // Call with parent and nested options
    handleMenuClose();
  };

  // Type-safe valueRenderer function
  const valueRenderer = (
    valueAsString: unknown,
    value: any,
    ...keyPath: any[]
  ) => {
    const path = keyPath
      .reverse()
      .map((key, index) =>
        Array.isArray(value) && !isNaN(Number(key)) // Check if the key is a numeric index
          ? `[${key}]`
          : key
      )
      .join(".")
      .replace(/\.\[/g, "[") // Replace ".[" with "[" for better formatting
      .replace(/\.\]/g, "]"); // Replace ".]" with "]" for better formatting

    const isRemove = (path: string): boolean => {
      // Extract the exact path by converting `.n` to `[n]`
      const exactPath = path.replace(/\.(\d+)/g, "[$1]");

      // Check for the original path and its presence in contactProperties
      const apiFieldNames = contactProperties?.map(
        (property) => property.api_field_name
      );

      // Check if the path exists in contactProperties
      const pathExists =
        contactProperties.some(
          (property) => property.api_field_name === exactPath
        ) || apiFieldNames?.includes(exactPath); // Match the exact path

      return pathExists;
    };

    const handleRemove = () => {
      handleSelectionChange(null, path, true, "contact"); // Only handle contact properties
    };

    return (
      <>
        {String(valueAsString)} {/* Safely handle the value as a string */}
        <IconButton
          onClick={(event) =>
            isRemove(path)
              ? handleRemove()
              : (setNestedMenuOpen(true), handleMenuOpen(event, path))
          }
          style={{ bottom: "1px" }}
        >
          {isRemove(path) ? <Remove /> : <Add />}
        </IconButton>
        {/* Menu for actions */}
        {!isRemove(path) && (
          <StyledMenu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && !nestedMenuOpen}
            onClose={handleMenuClose}
          >
            <StyledMenuItem onClick={() => handleMenuItemClick("contact")}>
              Save As Contact Properties
            </StyledMenuItem>
          </StyledMenu>
        )}
        {/* Nested Menu for additional options */}
        {nestedMenuOpen && (
          <StyledMenu
            anchorEl={anchorEl}
            open={nestedMenuOpen}
            onClose={handleMenuClose}
          >
            <StyledMenuItem
              onClick={() =>
                handleNestedMenuItemClick("all-index", isRemove(path))
              }
            >
              All Index
            </StyledMenuItem>
            <StyledMenuItem
              onClick={() =>
                handleNestedMenuItemClick("selected-index", isRemove(path))
              }
            >
              Selected Index
            </StyledMenuItem>
          </StyledMenu>
        )}
      </>
    );
  };

  return (
    <>
      {jsonData ? (
        <TreeContainer>
          <JSONTree
            data={jsonData}
            valueRenderer={valueRenderer}
            theme={theme||JsonViewTheme} // Use provided theme or default
            shouldExpandNodeInitially={shouldExpandNode} // Allow control over node expansion
          />
        </TreeContainer>
      ) : (
        ""
      )}
    </>
  );
};

export default JsonViewer;
